import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase, supabaseFunctions } from "@/integrations/supabase/client";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { useEmailAvailability } from "@/hooks/useEmailAvailability";
import { useTranslation } from "@/hooks/useTranslation";
import { toast } from "@/components/ui/sonner";
import { useSimplifiedAuth } from "@/contexts/SimplifiedAuthContext";
import { usePendingActionHandler } from "@/hooks/usePendingActionHandler";
import { Mail, AlertCircle, CheckCircle } from "lucide-react";
import { InlineLoader } from "@/components/ui/loading";
import { HeadingLarge, HeadingSmall, BodySmall, Caption } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import { logger } from "@/services/loggingService";

interface SharedAuthFormProps {
  onSuccess?: () => void;
  showMarketing?: boolean;
  compact?: boolean;
  redirectAfterAuth?: boolean;
  selectedRole?: 'nanny' | 'mother' | null;
}

export const SharedAuthForm = ({
  onSuccess,
  showMarketing = false,
  compact = false,
  redirectAfterAuth = true,
  selectedRole = null
}: SharedAuthFormProps) => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [lastResetRequest, setLastResetRequest] = useState<number | null>(null);
  const [emailCheckTimeout, setEmailCheckTimeout] = useState<NodeJS.Timeout | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [showHelpText, setShowHelpText] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [showNameCollection, setShowNameCollection] = useState(false);
  
  const toastService = useTranslatedToast();
  const { checkEmailAvailability, result: emailAvailability, isChecking: isCheckingEmail, clearResult } = useEmailAvailability();
  const { refetchData } = useSimplifiedAuth();
  const { processPendingAction } = usePendingActionHandler();

  // Check email availability with debouncing
  const debouncedEmailCheck = useCallback((email: string) => {
    if (emailCheckTimeout) {
      clearTimeout(emailCheckTimeout);
    }
    
    const timeout = setTimeout(() => {
      if (email && email.includes('@')) {
        checkEmailAvailability(email);
      }
    }, 500);
    
    setEmailCheckTimeout(timeout);
  }, [emailCheckTimeout, checkEmailAvailability]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }
    };
  }, [emailCheckTimeout]);
  
  // Cleanup force_name_collection flag on unmount if names were completed
  useEffect(() => {
    return () => {
      // If user completed names, the flag is already cleared
      // If they didn't and left the page, keep the flag so they see the prompt again
      if (showNameCollection && firstName.trim() && lastName.trim()) {
        localStorage.removeItem('force_name_collection');
      }
    };
  }, [showNameCollection, firstName, lastName]);

  // Close forgot password modal when email is sent
  useEffect(() => {
    if (resetEmailSent && showForgotPassword) {
      setShowForgotPassword(false);
    }
  }, [resetEmailSent, showForgotPassword]);

  // Progressive disclosure - show help text after 5 seconds
  useEffect(() => {
    if (emailSent) {
      const timer = setTimeout(() => setShowHelpText(true), 5000);
      return () => clearTimeout(timer);
    } else {
      setShowHelpText(false);
    }
  }, [emailSent]);

  // On mount, if name collection is forced and user is authenticated,
  // check profile and show name collection if names are missing
  useEffect(() => {
    let cancelled = false;

    const checkProfileForNames = async () => {
      try {
        const force = localStorage.getItem('force_name_collection') === 'true';
        if (!force) return;

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        let attempts = 0;
        const maxAttempts = 5;
        const delay = 150;

        while (attempts < maxAttempts && !cancelled) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('first_name, last_name')
            .eq('user_id', user.id as any)
            .maybeSingle();

          const hasFirstName = profile && 'first_name' in profile && profile.first_name && String(profile.first_name).trim() !== '';
          const hasLastName = profile && 'last_name' in profile && profile.last_name && String(profile.last_name).trim() !== '';

          if (hasFirstName && hasLastName) {
            try { localStorage.removeItem('force_name_collection'); } catch {}
            if (redirectAfterAuth) {
              navigate('/', { replace: true });
            } else if (onSuccess) {
              onSuccess();
            }
            return;
          }

          if (profile) {
            break;
          }

          attempts++;
          if (attempts < maxAttempts) {
            await new Promise(r => setTimeout(r, delay));
          }
        }

        if (!cancelled) {
          setShowNameCollection(true);
        }
      } catch (err) {
        if (!cancelled) {
          setShowNameCollection(true);
        }
      }
    };

    checkProfileForNames();
    return () => { cancelled = true; };
  }, [navigate, onSuccess, redirectAfterAuth]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setRateLimitError(null);

    try {
      // Send OTP to any email - no name required yet
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        }
      });

      if (error) {
        if (error.message.includes('rate limit')) {
          setRateLimitError(t('errors.rate_limit'));
        } else {
          toastService.raw.error(error.message);
        }
      } else {
        setPendingEmail(email);
        setEmailSent(true);
        toastService.success('auth.verification_code_sent');
      }
    } catch (error) {
      toastService.raw.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!otpCode || otpCode.length !== 6) {
      toastService.error('auth.code_invalid');
      return;
    }
    
    setOtpLoading(true);
    setRateLimitError(null);
    
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: pendingEmail,
        token: otpCode,
        type: 'email', // Changed from 'magiclink' - fixes expiration for new signups
      });
      
      if (error) {
        logger.error('OTP verification error', error, {
          component: 'SharedAuthForm',
          metadata: { errorCode: error.code }
        });
        
        // Check error.code first (most reliable)
        if (error.code === 'otp_expired') {
          toastService.error('auth.code_expired');
        } else if (error.code === 'validation_failed' || error.code === 'otp_disabled') {
          toastService.error('auth.code_invalid');
        } else if (error.message?.toLowerCase().includes('rate limit')) {
          setRateLimitError(t('errors.rate_limit'));
          toastService.error('auth.rate_limit');
        } else if (error.message?.toLowerCase().includes('expired')) {
          toastService.error('auth.code_expired');
        } else if (error.message?.toLowerCase().includes('invalid') || 
                   error.message?.toLowerCase().includes('token')) {
          toastService.error('auth.code_invalid');
        } else {
          toastService.error('auth.verification_failed');
        }
    } else if (data.session) {
      const user = data.user;
      
      // Set flag to prevent PreRouteGate from redirecting
      localStorage.setItem('force_name_collection', 'true');
      console.log('[Auth] OTP verified, checking profile with retry...');
      
      // Check profile with retry logic (up to 5 attempts)
      let attempts = 0;
      const maxAttempts = 5;
      const retryDelay = 150;
      
      while (attempts < maxAttempts) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('first_name, last_name')
          .eq('user_id', user.id as any)
          .maybeSingle();
        
        // Check if names exist
        const hasFirstName = profile && 'first_name' in profile && profile.first_name && String(profile.first_name).trim() !== '';
        const hasLastName = profile && 'last_name' in profile && profile.last_name && String(profile.last_name).trim() !== '';
        
        if (hasFirstName && hasLastName) {
          // Existing user with complete profile
          console.log('[Auth] Profile complete, checking for role assignment');
          localStorage.removeItem('force_name_collection');
          
          // If selectedRole is set, assign nanny/mother role for existing users
          if (selectedRole) {
            try {
              console.log('[Auth] Assigning role to existing user:', selectedRole);
              await supabaseFunctions.functions.invoke('assign-nanny-platform-role', {
                body: { userType: selectedRole }
              });
            } catch (err) {
              // Swallow duplicate role errors (idempotent)
              console.log('[Auth] Role assignment result:', err);
            }
            
            // Clear auth cache and refetch
            localStorage.removeItem('auth_roles_cache');
            await refetchData();
            
            // Small delay for role propagation
            await new Promise(resolve => setTimeout(resolve, 250));
            
            // Set workspace mode and redirect to nanny/mother dashboard
            localStorage.setItem('workspace_mode', selectedRole);
            const redirectPath = selectedRole === 'nanny' 
              ? '/workspace/nanny/dashboard'
              : '/workspace/mother/dashboard';
            console.log('[Auth] Redirecting existing user to:', redirectPath);
            navigate(redirectPath, { replace: true });
            return;
          }
          
          // Check for pending action first (deep link from email)
          const pendingAction = processPendingAction();
          if (pendingAction) {
            console.log('[Auth] Redirecting to pending action:', pendingAction.returnPath);
            return;
          }
          
          // Default redirect for non-nanny/mother flows
          if (redirectAfterAuth) {
            navigate("/", { replace: true });
          } else if (onSuccess) {
            onSuccess();
          }
          return;
        }
        
        // If profile exists but names are missing, stop retrying
        if (profile) {
          console.log('[Auth] Profile found but names missing, showing name collection');
          break;
        }
        
        // Profile not ready yet, retry
        attempts++;
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
      
      // Show name collection form
      console.log('[Auth] Showing name collection form');
      setEmailSent(true);
      setShowNameCollection(true);
      setOtpLoading(false);
      return;
    }
    } catch (error) {
      logger.error('Unexpected error during OTP verification', error as Error, {
        component: 'SharedAuthForm'
      });
      toastService.error('auth.verification_failed');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleCompleteProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName.trim() || !lastName.trim()) {
      toastService.error('auth.required_fields');
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('[Auth] Completing user profile via Edge Function');
      
      const { data, error } = await supabaseFunctions.functions.invoke('complete-user-signup', {
        body: {
          firstName: firstName.trim(),
          lastName: lastName.trim()
        }
      });

      if (error) {
        logger.error('Edge Function call failed', error, {
          component: 'SharedAuthForm'
        });
        toastService.error('error.generic');
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || 'Unknown error');
      }

      console.log('[Auth] Profile completed successfully:', data.profile);
      
      // Clear the force name collection flag
      localStorage.removeItem('force_name_collection');
      
      // Hide name collection form
      setShowNameCollection(false);
      
      // Small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // If nanny platform role is selected, assign role
      if (selectedRole) {
        console.log('[Auth] Assigning nanny platform role:', selectedRole);
        
        const { error: roleError } = await supabaseFunctions.functions.invoke(
          'assign-nanny-platform-role',
          {
            body: {
              userType: selectedRole
            }
          }
        );
        
        if (roleError) {
          console.error('Failed to assign role:', roleError);
          toastService.error('Failed to assign role. You can complete your profile later.');
          return; // Don't redirect if role assignment failed
        }
        
        console.log('[Auth] Role assigned successfully');
        
        // CRITICAL: Refresh auth data to update hasNannyAccess/hasMotherAccess flags
        console.log('[Auth] Refreshing auth data before redirect...');
        localStorage.removeItem('auth_roles_cache'); // Clear cache
        await refetchData(); // Force refresh
        
        // Wait for refresh to propagate
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Set workspace mode for future redirects
        localStorage.setItem('workspace_mode', selectedRole);
        
        toastService.success(
          selectedRole === 'nanny' 
            ? 'Welcome to the Nanny platform!' 
            : 'Welcome! Start finding nannies.'
        );
        
        // Redirect based on role - NOW auth context has updated roles
        const redirectPath = selectedRole === 'nanny' 
          ? '/workspace/nanny/dashboard'
          : '/workspace/mother/dashboard';
        
        console.log('[Auth] Redirecting to:', redirectPath);
        navigate(redirectPath, { replace: true });
        return;
      }
      
      // Check for pending action first (deep link from email)
      const pendingAction = processPendingAction();
      if (pendingAction) {
        console.log('[Auth] Redirecting to pending action after profile complete:', pendingAction.returnPath);
        return;
      }
      
      // Success - redirect to app (default behavior)
      if (redirectAfterAuth) {
        navigate("/", { replace: true });
      } else if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      logger.error('Error completing profile', error as Error, {
        component: 'SharedAuthForm'
      });
      toastService.error('error.generic');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendDisabled(true);
    
    try {
      await handleEmailAuth(new Event('submit') as any);
      toastService.success('auth.verification_code_sent');
    } catch (error) {
      toastService.error('error.generic');
    }
    
    // Disable resend for 60 seconds
    setTimeout(() => setResendDisabled(false), 60000);
  };


  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setRateLimitError(null);

    // Client-side rate limiting - prevent multiple requests within 60 seconds
    const now = Date.now();
    if (lastResetRequest && (now - lastResetRequest) < 60000) {
      const remainingTime = Math.ceil((60000 - (now - lastResetRequest)) / 1000);
      setRateLimitError(t('password_reset.rate_limit_wait', { seconds: remainingTime }));
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        if (error.message.includes('rate limit') || error.message.includes('Too many')) {
          setRateLimitError(t('password_reset.rate_limit_minutes'));
        } else {
          toastService.raw.error(error.message);
        }
      } else {
        setLastResetRequest(now);
        setResetEmailSent(true);
        toastService.success('auth.password_reset_sent');
      }
    } catch (error) {
      toastService.error('error.generic');
    } finally {
      setLoading(false);
    }
  };

  const HeadingComponent = compact ? "h1" : HeadingLarge;
  const DescriptionComponent = compact ? "p" : BodySmall;

  return (
      <div className={cn(
        "w-full",
        // Mobile: Use full width for maximum space, Desktop: Constrain for readability
        compact ? "max-w-none sm:max-w-sm sm:mx-auto space-y-2.5" : "max-w-sm space-y-4"
      )}>
      {/* Reset email sent confirmation */}
      {resetEmailSent && (
        <Alert variant="success" className="animate-fade-in-static">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('password_reset.success_title')}</AlertTitle>
          <AlertDescription>
            {t('password_reset.success')}
          </AlertDescription>
        </Alert>
      )}

      {/* Rate limit error alert */}
      {rateLimitError && (
        <Alert variant="destructive" className="animate-fade-in-static">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('error.rate_limit_title')}</AlertTitle>
          <AlertDescription>
            {rateLimitError}
          </AlertDescription>
        </Alert>
      )}

      {/* Name Collection Screen - After OTP verification */}
      {showNameCollection && (
        <div className="space-y-3 animate-fade-in-static">
            <div className="text-center space-y-2">
              <HeadingSmall>{t('auth:profile_completion.title')}</HeadingSmall>
              <BodySmall className="text-muted-foreground">
                {t('auth:profile_completion.description')}
              </BodySmall>
            </div>
            
            <form onSubmit={handleCompleteProfile} className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">{t('auth:profile_completion.first_name')}</Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">{t('auth:profile_completion.last_name')}</Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className={cn(
                  "w-full h-11 sm:h-10 transition-opacity duration-300",
                  (firstName.trim() && lastName.trim()) ? "opacity-100" : "opacity-70"
                )}
                disabled={loading || !firstName.trim() || !lastName.trim()}
              >
                {loading ? (
                  <InlineLoader size="sm" variant="pulse" text={t('auth:profile_completion.completing')} />
                ) : (
                  t('auth:profile_completion.complete_profile')
                )}
              </Button>
            </form>
        </div>
      )}

    {/* OTP Input Screen - Modern, seamless design */}
    {emailSent && !showNameCollection && (
      <div className="space-y-3 animate-fade-in-static">
          {/* Simple header - no card, no gradient */}
          <div className="text-center space-y-2">
            <HeadingSmall className="text-foreground">
              {t('auth:otp_verification.title')}
            </HeadingSmall>
            <BodySmall className="text-muted-foreground">
              {t('auth:otp_verification.code_sent_to')} <span className="font-medium text-foreground">{pendingEmail}</span>
            </BodySmall>
          </div>
          
          {/* OTP Input - Prominent and centered */}
          <div className="flex justify-center py-1">
            <InputOTP
              maxLength={6}
              value={otpCode}
              onChange={(value) => setOtpCode(value)}
              disabled={otpLoading}
            >
            <InputOTPGroup className="gap-1.5">
              <InputOTPSlot index={0} className="h-11 w-11 sm:h-12 sm:w-12 text-base sm:text-lg" />
              <InputOTPSlot index={1} className="h-11 w-11 sm:h-12 sm:w-12 text-base sm:text-lg" />
              <InputOTPSlot index={2} className="h-11 w-11 sm:h-12 sm:w-12 text-base sm:text-lg" />
              <InputOTPSlot index={3} className="h-11 w-11 sm:h-12 sm:w-12 text-base sm:text-lg" />
              <InputOTPSlot index={4} className="h-11 w-11 sm:h-12 sm:w-12 text-base sm:text-lg" />
              <InputOTPSlot index={5} className="h-11 w-11 sm:h-12 sm:w-12 text-base sm:text-lg" />
            </InputOTPGroup>
            </InputOTP>
          </div>
          
          {/* Primary Action - Full width, prominent */}
          <Button
            onClick={handleVerifyOtp}
            disabled={otpLoading || otpCode.length !== 6}
            className={cn(
              "w-full h-11 sm:h-10 transition-opacity duration-300",
              otpCode.length === 6 ? "opacity-100" : "opacity-70"
            )}
            size="lg"
          >
            {otpLoading ? (
              <InlineLoader size="sm" variant="pulse" text={t('auth:otp_verification.verifying')} />
            ) : (
              t('auth:otp_verification.verify_continue')
            )}
          </Button>
          
          {/* Helper actions - Progressive disclosure */}
          <div className="space-y-2">
            {/* Always render help text container - just hide when not shown */}
            <div className="h-8 flex items-center justify-center">
              <Caption className={cn(
                "text-center text-muted-foreground transition-opacity duration-300",
                showHelpText ? "opacity-100" : "opacity-0"
              )}>
                {t('auth:otp_verification.didnt_receive')}
              </Caption>
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center justify-center gap-1 text-sm">
              <Button
                type="button"
                variant="link"
                size="sm"
                className="text-muted-foreground h-auto p-0"
                onClick={handleResendCode}
                disabled={resendDisabled}
              >
                {resendDisabled ? t('auth:otp_verification.resending') : t('auth:otp_verification.resend_code')}
              </Button>
              <span className="text-muted-foreground">•</span>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="text-muted-foreground h-auto p-0"
                onClick={() => {
                  setEmailSent(false);
                  setOtpCode("");
                  setPendingEmail("");
                  setShowHelpText(false);
                }}
              >
                {t('auth:otp_verification.use_different_email')}
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Email Form - hide when OTP is active or collecting names */}
      {!emailSent && !showNameCollection && (
          <form onSubmit={handleEmailAuth} className={cn(compact ? "space-y-3" : "space-y-4", "transition-all duration-300 ease-in-out")}>
            {/* Dynamic separator at top of form */}
            <div className="relative mb-3">
              <div className="absolute inset-0 flex items-center">
                <Separator className="bg-foreground/20" />
              </div>
              <div className="relative flex justify-center">
                <Caption className="bg-background px-2 text-muted-foreground">
                  {emailAvailability?.status === 'available' 
                    ? t('visual_separator.create_account')
                    : t('visual_separator.signin_continue')
                  }
                </Caption>
              </div>
            </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t('signup.email')}</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder={t('signup.email')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearResult();
                debouncedEmailCheck(e.target.value);
              }}
              className="pl-10 pr-10"
              required
            />
            {/* Inline validation indicators - subtle and native */}
            {isCheckingEmail && email.includes('@') && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <InlineLoader size="xs" variant="pulse" />
              </div>
            )}
            {!isCheckingEmail && emailAvailability && email.includes('@') && (
              <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-600 animate-fade-in-static" />
            )}
          </div>
        </div>

        {/* Always render button to reserve space */}
        <Button
          type="submit"
          className={cn(
            "w-full h-12 sm:h-11 transition-opacity duration-300",
            (!isCheckingEmail && emailAvailability !== null) 
              ? "opacity-100" 
              : "opacity-0 pointer-events-none"
          )}
          disabled={loading || !!rateLimitError || emailSent || isCheckingEmail || emailAvailability === null}
        >
          {loading ? (
            <InlineLoader 
              size="sm"
              variant="pulse"
              text={t('auth_methods.sending_code')}
            />
          ) : (
            t('auth_methods.send_verification_code')
          )}
        </Button>
        
        {/* Privacy & Terms disclaimer */}
        <div className="text-center mt-4">
          <Caption className="text-muted-foreground">
            {t('privacy.disclaimer')}
          </Caption>
          <div className="flex items-center justify-center gap-1.5 mt-1">
            <a 
              href={`${window.location.origin}/terms-of-service`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline"
            >
              {t('privacy.terms')}
            </a>
            <span className="text-xs text-muted-foreground">•</span>
            <a 
              href={`${window.location.origin}/privacy-policy`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline"
            >
              {t('privacy.privacy')}
            </a>
          </div>
        </div>
          </form>
      )}

      {/* Password Reset Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('password_reset.title')}</DialogTitle>
            <DialogDescription>
              {t('password_reset.description')}
              <br />
              <span className="text-xs text-muted-foreground mt-2 block">
                {t('password_reset.magic_link_note')}
              </span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">{t('signup.email')}</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder={t('signup.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 sm:h-10"
              disabled={loading || !email}
            >
              {loading ? (
                <InlineLoader size="sm" variant="pulse" text={t('password_reset.button')} />
              ) : (
                t('password_reset.button')
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
